import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@eombor/ui';

export interface MenuProps {
  label: string;
  /** Tugma ichidagi ko'rinish. */
  trigger: ReactNode;
  triggerClassName?: string;
  /** Panel kengligi qo'ng'iroq va hisob menyusida farq qiladi. */
  panelClassName?: string;
  /**
   * Panelning ARIA roli.
   *
   * `menu` — ichida buyruq qatorlari bo'lganda (chiqish, tarix). Sozlamalar
   * paneli uchun esa `group`: u yerda radio guruhlar turadi va ular `menu`
   * ning yaroqli bolalari emas — ekran o'qigich "menyu, ichida elementi
   * yo'q" deb o'qirdi.
   */
  popupRole?: 'menu' | 'group';
  children: ReactNode;
}

/**
 * Ustiga ochiladigan kichik panel. `Esc` va tashqariga bosish yopadi, fokus
 * qaytadi — modal emas, shuning uchun fokus qopqoni yo'q (`Dialog` dan farqi).
 *
 * Alohida faylda turadi, chunki uni ikki joy ishlatadi: yuqori qatordagi
 * bildirishnoma va hisob menyulari hamda sozlamalar menyusi. Ikki nusxa
 * bo'lganda ularning yopilish qoidasi asta-sekin bir-biridan uzoqlashardi.
 */
export const Menu = ({
  label,
  trigger,
  triggerClassName,
  panelClassName,
  popupRole = 'menu',
  children,
}: MenuProps) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (target instanceof Node && rootRef.current?.contains(target) === true) return;
      setOpen(false);
    };

    const onKeyDown = (event: globalThis.KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      triggerRef.current?.focus();
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-haspopup={popupRole === 'menu' ? 'menu' : true}
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className={cn(
          'flex items-center gap-2 rounded-[var(--radius-sm)] text-text-muted',
          'transition-[background-color,color,transform] duration-[var(--motion-fast)]',
          'ease-[var(--ease-standard)] hover:bg-surface-2 hover:text-text',
          'active:scale-[0.96]',
          triggerClassName,
        )}
      >
        {trigger}
      </button>

      {open ? (
        <div
          role={popupRole}
          aria-label={label}
          className={cn(
            'motion-pop-in absolute right-0 top-[calc(100%+0.5rem)] z-[var(--z-dropdown)]',
            'overflow-hidden rounded-[var(--radius-md)] border border-border',
            'bg-surface shadow-[var(--shadow-3)]',
            panelClassName,
          )}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
};
